import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  IconButton,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import { CloseRounded } from '@mui/icons-material';

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.25;

const isPdfUrl = (url: string): boolean => {
  try {
    const path = url.split('?')[0];
    return path.toLowerCase().endsWith('.pdf');
  } catch {
    return false;
  }
};

export interface ImagePreviewModalProps {
  open: boolean;
  onClose: () => void;
  images: string[];
  initialIndex?: number;
  /** Optional custom title (e.g. "Bill Preview" or expense description) */
  titleLabel?: string;
}

export default function ImagePreviewModal({
  open,
  onClose,
  images,
  initialIndex = 0,
  titleLabel,
}: ImagePreviewModalProps) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [rotate, setRotate] = useState(0);

  useEffect(() => {
    if (open) {
      setCurrentIndex(Math.min(initialIndex, Math.max(0, images.length - 1)));
      setZoom(1);
      setRotate(0);
    }
  }, [open, initialIndex, images.length]);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
    setZoom(1);
    setRotate(0);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
    setZoom(1);
    setRotate(0);
  };

  const handleZoomIn = () => {
    setZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP));
  };

  const handleZoomOut = () => {
    setZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP));
  };

  const handleRotate = () => {
    setRotate((r) => (r + 90) % 360);
  };

  const handleDownload = () => {
    if (!currentItem) return;
    const path = currentItem.split('?')[0];
    const suggestedName = path.split('/').pop() || `preview-${currentIndex + 1}${isPdfUrl(currentItem) ? '.pdf' : '.jpg'}`;
    const link = document.createElement('a');
    link.href = currentItem;
    link.download = suggestedName;
    link.rel = 'noopener noreferrer';
    link.target = '_blank';
    link.click();
  };

  const currentItem = images[currentIndex];
  const isPdf = currentItem ? isPdfUrl(currentItem) : false;
  const hasMultiple = images.length > 1;
  const title = titleLabel ?? `Image Preview (${currentIndex + 1} / ${images.length})`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <span>{title}</span>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          {!isPdf && (
            <>
              <IconButton size="small" onClick={handleZoomOut} title="Zoom out" aria-label="Zoom out">
                <ZoomOutIcon />
              </IconButton>
              <IconButton size="small" onClick={handleZoomIn} title="Zoom in" aria-label="Zoom in">
                <ZoomInIcon />
              </IconButton>
              <IconButton size="small" onClick={handleRotate} title="Rotate" aria-label="Rotate">
                <RotateRightIcon />
              </IconButton>
            </>
          )}
          {currentItem && (
            <>
              <Button
                size="small"
                color="primary"
                startIcon={<DownloadIcon />}
                onClick={handleDownload}
              >
              </Button>
              <Button
                size="small"
                color="primary"
                startIcon={<OpenInNewIcon />}
                onClick={() => window.open(currentItem, '_blank')}
              >
              </Button>
            </>
          )}
          <Button size="small" color="inherit" startIcon={<CloseRounded />} onClick={onClose}></Button>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: fullScreen ? '100%' : '400px',
          flex: fullScreen ? 1 : undefined,
          position: 'relative',
          p: 1,
          overflow: 'hidden',
        }}
      >
        {hasMultiple && (
          <IconButton
            onClick={handlePrev}
            sx={{
              position: 'absolute',
              left: 8,
              zIndex: 1,
              bgcolor: 'rgba(0,0,0,0.3)',
              color: '#fff',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' },
            }}
            aria-label="Previous image"
          >
            <ArrowBackIosNewIcon />
          </IconButton>
        )}
        {currentItem && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              minHeight: fullScreen ? '100%' : '70vh',
              height: fullScreen ? '100%' : undefined,
              overflow: 'auto',
            }}
          >
            {isPdf ? (
              <Box
                component="iframe"
                src={currentItem}
                title={`Preview ${currentIndex + 1}`}
                sx={{
                  width: '100%',
                  minHeight: fullScreen ? '100%' : '70vh',
                  height: fullScreen ? '100%' : '70vh',
                  border: 'none',
                  flex: 1,
                }}
              />
            ) : (
              <img
                src={currentItem}
                alt={`Preview ${currentIndex + 1}`}
                style={{
                  maxWidth: '100%',
                  maxHeight: fullScreen ? '100%' : '70vh',
                  objectFit: 'contain',
                  borderRadius: '4px',
                  transform: `scale(${zoom}) rotate(${rotate}deg)`,
                  transition: 'transform 0.2s ease',
                }}
                draggable={false}
              />
            )}
          </Box>
        )}
        {hasMultiple && (
          <IconButton
            onClick={handleNext}
            sx={{
              position: 'absolute',
              right: 8,
              zIndex: 1,
              bgcolor: 'rgba(0,0,0,0.3)',
              color: '#fff',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' },
            }}
            aria-label="Next image"
          >
            <ArrowForwardIosIcon />
          </IconButton>
        )}
      </DialogContent>
    </Dialog>
  );
}
